import React, {
  useEffect,
  useRef,
  useState,
  useMemo,
  lazy,
  Suspense,
} from "react";
import {
  AssignedToSelect,
  ClientCard,
  DetailSidebarLayout,
  TaskList,
  StatusBadge,
  JobTagsInline,
} from "../../../components";
import {
  Button,
  Badge,
  Toggle,
  Avatar,
  EditableText,
  Modal,
  Dropdown,
  ButtonDropdown,
  Spinner,
} from "../../../shared";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import JobInfo from "./Info";
import Accessories from "./Accessories";
import {
  useAuth,
  useDebounceSearch,
  useJob,
  JobProvider,
  useCrumbs,
  useConfig,
  useForm,
  useJobLiveUpdates,
} from "../../../hooks";
import { jobUpdated } from "../../../redux/reducers/jobs/jobSlice";
import { useDispatch, useSelector } from "react-redux";
import { FORM_OPTIONS } from "../table.config";
import SidebarContent from "./SidebarContent";
import {
  ChevronDoubleLeftIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  EllipsisHorizontalIcon,
  EllipsisVerticalIcon,
} from "@heroicons/react/24/outline";
import request, { renderQueries } from "../../../utils/request";
import {
  getNextStage,
  getStage,
  normalizeJobStatusForStages,
} from "../job-stages";
import { camelCase } from "lodash";
import { toast } from "react-toastify";
import {
  useDeleteJobsMutation,
  useGetAdjacentJobsQuery,
  useGetJobQuery,
  useUpdateGlobalJobRatesMutation,
  useUpdateJobRatesMutation,
  useUpdateJobMutation,
} from "../../../redux/services/jobs/jobsApi";
import { useGetLegsQuery } from "@/redux/services/legs/legsApi";
import { useAddLegMutation } from "@/redux/services/legs/legsApi";
import { useAddInvoiceMutation } from "../../../redux/services/invoices/invoicesApi";
import { capitalizeFirstLetter } from "../../../utils";
import { isFeatureEnabledFromConfig } from "../../../utils/featureFlags";
import { mergeJobLegsWithFetched } from "../../../utils/leg-display";
import { usePermissions } from "../../../hooks/usePermissions";
import { PERMISSIONS } from "../../../constants/access-control";
import { getProductTerminology } from "../../../constants/product-terminology";
import JobEquipment from "./Equipment";
import JobSheetPreview, {
  previewFileButtonLabel,
} from "./JobSheetPreview";
import { readJobListQuery } from "../jobListQuery";
import {
  getRateUpdatePayloads,
  sanitizeJobUpdateData,
} from "./utils.jobUpdate";
import { useGetEmployeesQuery } from "../../../redux/services/employees/employeesApi";
import { useAddTemplateMutation } from "../../../redux/services/templates/api";
import { useApproveJobItemsMutation } from "@/redux/services/jobs/jobItems";
import { renderValidations } from "@/shared/Table/table.utils";
import JobQuotes from "./Quotes";
import {
  DeleteJobModal,
  TemplateModal,
  JobActionModal,
  ScheduleJobModal,
  DispatchJobModal,
  CompleteJobModal,
} from "../../../modals";
import { JobRequirementAlerts } from "../../../components/RequirementAlertBanners";

// Create a reusable modal wrapper with loading state
const LazyModal = ({ children, ...props }) => (
  <Suspense
    fallback={
      <Modal {...props} content={<Spinner size="lg" className="m-auto" />} />
    }
  >
    {children}
  </Suspense>
);

const JobsShow = () => {
  const params = useParams();

  return (
    // Key by route param so local UI state (edit mode, footers, baselines) resets cleanly
    // when navigating between jobs (prevents one-render "flash" from previous job state).
    <JobProvider key={params.id} jobId={params.id}>
      <JobsShowDetails />
    </JobProvider>
  );
};

// Move this outside the component or memoize it if it needs component scope
const getModalContentForStage = (stage, props) => {
  if (!stage?.modal) return "";

  if (React.isValidElement(stage.modal)) {
    return stage.modal;
  }

  if (typeof stage.modal === "function") {
    return stage.modal(props);
  }

  return stage.modal;
};

// Move this outside the component to avoid recreating on each render
const getValidationsForStatus = (status) => {
  switch (status) {
    case "Booked":
      return renderValidations([{ id: "assignedTo", required: true }]);
    case "Pending":
      return renderValidations([
        { id: "appointmentDate", required: true },
        { id: "selectedItems", required: true },
      ]);
    default:
      return renderValidations([]); // Always return a consistent structure
  }
};

/**
 * Icon control for stepping to the previous or next job.
 * Greyed out when that side of the list has no job.
 *
 * @param {object} props
 * @param {string} props.label
 * @param {boolean} props.disabled
 * @param {() => void} props.onClick
 * @param {import("react").ReactNode} props.children
 */
function JobNeighborButton({ label, disabled, onClick, children }) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      disabled={disabled}
      onClick={onClick}
      className={`rounded p-0.5 ${
        disabled
          ? "cursor-not-allowed text-gray-300"
          : "cursor-pointer text-gray-800 hover:bg-gray-100"
      }`}
    >
      {children}
    </button>
  );
}

const JobsShowDetails = () => {
  // Library Hooks
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();

  // Custom Hooks
  const { user } = useAuth();
  const { configs, appType } = useConfig();
  const terms = getProductTerminology(appType);
  const { hasPermission, hasAnyPermission } = usePermissions();
  const canEditJobs = hasPermission(PERMISSIONS.JOBS_EDIT);
  const canAssignJobs = hasPermission(PERMISSIONS.JOBS_ASSIGN);
  const canEditOrAssign = hasAnyPermission([
    PERMISSIONS.JOBS_EDIT,
    PERMISSIONS.JOBS_ASSIGN,
  ]);
  const canViewInvoices = hasPermission(PERMISSIONS.INVOICES_READ);
  const crumbs = useCrumbs();
  const { setRightContent: setCrumbsRightContent } = crumbs;

  const featureFlags = configs?.featureFlags || {};
  const isFeatureEnabled = (featureKey) =>
    isFeatureEnabledFromConfig(configs, featureKey);
  const isDispatchEnabled = isFeatureEnabled("jobs.actions.dispatch.enabled");
  const liveUpdatesConfig = configs?.features?.Job?.liveUpdates || {};
  const liveUpdatesFlag = featureFlags?.["jobs.liveUpdates.enabled"];
  const liveUpdatesEnabled =
    typeof liveUpdatesFlag === "boolean"
      ? liveUpdatesFlag
      : liveUpdatesConfig?.enabled === true;
  const liveUpdatesPollingInterval =
    Number(liveUpdatesConfig?.pollingIntervalMs) || 20000;

  // React Hooks
  const [loading, setLoading] = useState(true);
  const [modalLoad, setModalLoad] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [isEditing, setisEditing] = useState(false);
  const [secondaryButtonLoading, setSecondaryButtonLoading] = useState(false);
  const [mainActionModal, setMainActionModal] = useState(false);
  const [scheduleJobModalOpen, setScheduleJobModalOpen] = useState(false);
  const [dispatchJobModalOpen, setDispatchJobModalOpen] = useState(false);
  const [completeJobModalOpen, setCompleteJobModalOpen] = useState(false);
  const [deleteVisible, setDeleteVisible] = useState(false);
  const [createInvoiceVisible, setCreateInvoiceVisible] = useState(false);
  const [assignedUsers, setAssignedUsers] = useState([]);
  const [hasUnsavedRates, setHasUnsavedRates] = useState(false);
  const [savingRateToggles, setSavingRateToggles] = useState(false);
  const [rateTogglesBaseline, setRateTogglesBaseline] = useState({
    useGlobalRates: false,
    useFlatRate: false,
  });
  // Snapshot of the form taken when the user enters edit mode. Used to:
  //  - compare against the live form to compute "what's changed"
  //  - revert the form when the user clicks Discard
  // Captured for any stage / appType so every edit session has a Cancel path.
  const [editBaselineForm, setEditBaselineForm] = useState(null);
  const [editBaselineKey, setEditBaselineKey] = useState("");
  const [pendingLegCreates, setPendingLegCreates] = useState([]);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  const [validations, setValidations] = useState(() => renderValidations([]));
  const [allEmployees, setAllEmployees] = useState([]);
  const [{ pageIndex, pageSize }, setPagination] = useState({
    pageIndex: 0,
    pageSize: 20,
  });

  const [sheetPreviewOpen, setSheetPreviewOpen] = useState(false);

  const isServiceRepair = appType === "serviceRepair";

  /**
   * Debug helper (opt-in):
   * In the browser console run:
   *   localStorage.setItem("debugRates", "true"); location.reload();
   * To turn off:
   *   localStorage.removeItem("debugRates");
   */
  const shouldDebugRates =
    typeof window !== "undefined" &&
    window?.localStorage?.getItem("debugRates") === "true";
  const lastRatesDebugKeyRef = useRef("");
  const isEmptyRateValue = (v) => v === null || v === undefined || v === "";

  // Form hook
  const { form, setFormInit, clearForm, updateForm, isValid } = useForm(
    isServiceRepair ? { validations } : {},
  );

  const {
    data: jobData,
    isQueryLoading,
    isError: isJobError,
    isJobUpdating,
    jobId: _id,
    isNew,
    isPending,
    isBooked,
    isCompleted,
    isActive: isJobActive,
    getItemsByType,
    approveJobItems,
  } = useJob();

  const [addInvoice, { isLoading: addingInvoice }] = useAddInvoiceMutation();
  const [updateJob, { isLoading: jobUpdating }] = useUpdateJobMutation();
  const [addLeg] = useAddLegMutation();
  const [updateGlobalJobRates, { isLoading: globalRatesUpdating }] =
    useUpdateGlobalJobRatesMutation();
  const [updateJobRates] = useUpdateJobRatesMutation();

  const job = jobData || {};
  const jobParamId = params?.id;
  const jobListQuery = useMemo(
    () => readJobListQuery(location.search),
    [location.search],
  );
  const { data: adjacentJobs } = useGetAdjacentJobsQuery(
    { id: jobParamId, ...jobListQuery },
    { skip: !jobParamId },
  );
  const previousJobId = adjacentJobs?.previous?._id;
  const nextJobId = adjacentJobs?.next?._id;

  useJobLiveUpdates({
    jobId: job?._id || jobParamId,
    enabled: liveUpdatesEnabled,
  });

  useGetJobQuery(
    { jobId: jobParamId, drivers: true },
    {
      skip: !jobParamId,
      pollingInterval: liveUpdatesEnabled ? liveUpdatesPollingInterval : 0,
      refetchOnFocus: liveUpdatesEnabled,
      refetchOnReconnect: liveUpdatesEnabled,
    },
  );

  const { search, handleSearch } = useDebounceSearch();

  // Modify the employees query to include pagination and search
  const {
    data: employeesData,
    isLoading: loadingEmployees,
    isFetching: fetchingMoreEmployees,
  } = useGetEmployeesQuery({
    role: "Technician",
    page: pageIndex + 1,
    limit: pageSize,
    search: search.useSearch,
    searchTerm: search.term,
  });

  // Update validations when job status changes
  useEffect(() => {
    if (job.status) {
      setValidations(getValidationsForStatus(job.status));
    }
  }, [job.status]);

  // Reset pagination and employees list when search changes
  useEffect(() => {
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    setAllEmployees([]); // Reset the accumulated list
  }, [search.term]);

  // Update allEmployees when new data comes in
  useEffect(() => {
    if (employeesData?.employees) {
      if (pageIndex === 0) {
        // Reset the list if it's the first page
        setAllEmployees(employeesData.employees);
      } else {
        // Append new employees to existing list
        setAllEmployees((prev) => {
          const newEmployees = employeesData.employees.filter(
            (newEmp) =>
              !prev.some((existingEmp) => existingEmp._id === newEmp._id),
          );
          return [...prev, ...newEmployees];
        });
      }
    }
  }, [employeesData?.employees, pageIndex]);

  // Function to load more data
  const handleLoadMoreEmployees = (nextPage) => {
    setPagination((prev) => ({
      ...prev,
      pageIndex: nextPage - 1, // Subtract 1 because pageIndex is 0-based
    }));
  };

  // Update form helper
  function onFormChange(e) {
    const { value, id } = e;
    updateForm({ value, id });
  }

  // Constant Variables
  const jobId = job.jobId || job.loadNumber || job.orderNumber;
  const editing = isEditing;
  const stageId = normalizeJobStatusForStages(job.status, appType);
  const stage = getStage(stageId, appType);

  const { data: legsData } = useGetLegsQuery(
    { jobId: job?._id, limit: 500, driver: true, route: true },
    {
      skip: appType !== "trucking" || !job?._id,
      pollingInterval: liveUpdatesEnabled ? liveUpdatesPollingInterval : 0,
      refetchOnFocus: liveUpdatesEnabled,
      refetchOnReconnect: liveUpdatesEnabled,
    },
  );

  const legsForDispatch = useMemo(() => {
    // Prefer live legs query (keeps CTA in sync after dispatching) with a safe fallback.
    return legsData?.legs || job?.legs || [];
  }, [job?.legs, legsData?.legs]);

  const jobWithAllLegs = useMemo(
    () => mergeJobLegsWithFetched(job, legsData?.legs),
    [job, legsData?.legs],
  );

  const isTruckingScheduledStage =
    appType === "trucking" && stageId === "scheduled";
  const deferLegCreate = isTruckingScheduledStage && editing;

  // If a trucking job is already "dispatched" but still has unassigned legs,
  // keep the header CTA as "Dispatch" (so dispatching can continue until all legs are assigned).
  const hasTruckingDispatchableLegs = useMemo(() => {
    if (appType !== "trucking") return false;
    const legs = Array.isArray(legsForDispatch) ? legsForDispatch : [];
    return legs.some((leg) => {
      const status = String(leg?.status || "").toLowerCase();
      const isDeliveredLike =
        status === "delivered" || status === "completed" || status === "paid";

      const driverId =
        leg?.driver?._id || leg?.driver?.id || leg?.driverId || leg?.driver;

      return !isDeliveredLike && !driverId;
    });
  }, [appType, legsForDispatch]);

  const stableStringify = useMemo(() => {
    const normalize = (value) => {
      if (value instanceof Date) return value.toISOString();
      if (Array.isArray(value)) return value.map(normalize);
      if (value && typeof value === "object") {
        const keys = Object.keys(value).sort();
        const out = {};
        keys.forEach((k) => {
          out[k] = normalize(value[k]);
        });
        return out;
      }
      return value;
    };
    return (value) => JSON.stringify(normalize(value));
  }, []);

  const formatFieldLabel = (key) => {
    const spaced = String(key || "")
      .replace(/_/g, " ")
      .replace(/([a-z])([A-Z])/g, "$1 $2");
    return capitalizeFirstLetter(spaced);
  };

  const formatFieldValue = (value) => {
    if (value === null || value === undefined) return "—";
    if (value instanceof Date) return value.toLocaleString();
    if (typeof value === "boolean") return value ? "On" : "Off";
    if (typeof value === "number")
      return Number.isFinite(value) ? String(value) : "—";
    if (typeof value === "string") return value.trim() ? value : "—";
    try {
      const s = JSON.stringify(value);
      return s && s !== "null" ? s : "—";
    } catch {
      return String(value);
    }
  };

  const getComparableJobEditState = (rawForm) => {
    if (!rawForm || typeof rawForm !== "object") return {};
    // Avoid noisy/large fields that shouldn't gate the Scheduled → Dispatch action.
    // (Rates have their own footer; items/legs are managed elsewhere.)
    // eslint-disable-next-line no-unused-vars
    const {
      items,
      legs,
      timeline,
      currentStatus,
      currentApproval,
      invoiceId,
      status,
      __v,
      createdAt,
      updatedAt,
      ...rest
    } = rawForm;
    return rest;
  };

  // Comparable view of the form used by the edit footer to summarize "what's
  // changed". Computed for every editing context (not only trucking-scheduled)
  // so the footer can render a useful summary regardless of stage/appType.
  const editComparable = useMemo(
    () => (editing ? getComparableJobEditState(form) : {}),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [editing, form],
  );

  const editChanges = useMemo(() => {
    if (!editing) return [];
    if (!editBaselineForm) return [];

    const before = getComparableJobEditState(editBaselineForm);
    const after = editComparable;

    const keys = Array.from(
      new Set([...Object.keys(before || {}), ...Object.keys(after || {})]),
    ).sort();

    const changes = [];
    for (const key of keys) {
      const b = before?.[key];
      const a = after?.[key];
      if (stableStringify(b) === stableStringify(a)) continue;
      changes.push({
        id: key,
        label: formatFieldLabel(key),
        before: b,
        after: a,
      });
    }

    // Pending leg drafts are trucking-only and stay no-op everywhere else.
    if (pendingLegCreates.length > 0) {
      changes.push({
        id: "__legs_added",
        label: "Legs added",
        before: 0,
        after: pendingLegCreates
          .map((p) => p?.label)
          .filter(Boolean)
          .join(", "),
      });
    }
    return changes;
  }, [
    editing,
    editBaselineForm,
    editComparable,
    stableStringify,
    pendingLegCreates,
  ]);

  const editDirty = useMemo(() => {
    if (!editing) return false;
    if (!editBaselineForm) return false;
    const current = stableStringify(editComparable);
    const baseline = stableStringify(
      getComparableJobEditState(editBaselineForm),
    );
    return current !== baseline || pendingLegCreates.length > 0;
  }, [
    editing,
    editBaselineForm,
    editComparable,
    stableStringify,
    pendingLegCreates.length,
  ]);

  const prevEditingRef = useRef(false);
  useEffect(() => {
    // Reset baseline when switching jobs so prior edit context doesn't leak.
    if (!job?._id) return;
    const nextKey = String(job._id);
    if (editBaselineKey && editBaselineKey !== nextKey) {
      setEditBaselineForm(null);
      setEditBaselineKey(nextKey);
      prevEditingRef.current = false;
    }
    if (!editBaselineKey) {
      setEditBaselineKey(nextKey);
    }
  }, [job?._id, editBaselineKey]);

  useEffect(() => {
    // Capture baseline whenever the user enters edit mode (any stage / appType)
    // so the footer always has something to revert to and the user can Cancel
    // without first making a change.
    const justEnteredEdit = editing && !prevEditingRef.current;
    prevEditingRef.current = editing;
    if (!justEnteredEdit) return;
    if (!job?._id || !form?._id) return;
    if (String(job._id) !== String(form._id)) return;

    setEditBaselineForm(form);
  }, [editing, job?._id, form?._id]);

  /* 
    Helper function to get rate based on current job options
  */
  function renderRate(name, useGlobal) {
    /**
     * Global rates behavior:
     * - If "Use Global Rates" is enabled, prefer global.
     * - Otherwise, prefer client-specific, BUT fall back to global when a client rate is missing.
     *
     * This matches the Settings > Rates copy: "fallback if none are set for a client".
     */
    const globalRate = configs?.rates?.[name];
    const clientRate = job?.client?.rates?.[name];

    const preferGlobal =
      !!useGlobal || !!form.useGlobalRates || !!job.useGlobalRates;
    return preferGlobal
      ? (globalRate ?? clientRate)
      : (clientRate ?? globalRate);
  }

  // --- Rate toggles (Global Rates / Flat Rate) draft + save/discard ---
  function renderRateFromSource(name, useGlobal) {
    const globalRate = configs?.rates?.[name];
    const clientRate = job?.client?.rates?.[name];
    // Even when explicitly choosing a source, still provide a safe fallback
    // so the UI doesn't show blank values if the selected source has no value.
    return useGlobal ? (globalRate ?? clientRate) : (clientRate ?? globalRate);
  }

  // Debug rates on Job Show (opt-in via localStorage flag).
  useEffect(() => {
    if (!shouldDebugRates) return;
    if (!job?._id) return;

    const key = [
      job._id,
      appType,
      String(!!configs?.rates),
      String(!!job?.client?.rates),
      String(!!job?.useGlobalRates),
      String(!!form?.useGlobalRates),
      String(!!job?.useFlatRate),
      String(!!form?.useFlatRate),
      // Include the actual form values so we can confirm hydration happened.
      String(form?.fuelRate),
      String(form?.baseRate),
      String(form?.flatRate),
    ].join("|");

    // Avoid spamming: log once per meaningful state change.
    if (lastRatesDebugKeyRef.current === key) return;
    lastRatesDebugKeyRef.current = key;

    const safePick = (obj, keys) => {
      const out = {};
      keys.forEach((k) => {
        out[k] = obj?.[k];
      });
      return out;
    };

    const rateKeysToCheck = [
      "baseRate",
      "fuelRate",
      "flatRate",
      "base",
      "fuel",
    ];
    const globalRates = configs?.rates || {};
    const clientRates = job?.client?.rates || {};

    // eslint-disable-next-line no-console
    console.groupCollapsed(
      `[JobShow][RatesDebug] job=${job._id} appType=${appType}`,
    );
    // eslint-disable-next-line no-console
    console.log("useGlobalRates (job/form):", {
      job: !!job?.useGlobalRates,
      form: !!form?.useGlobalRates,
    });
    // eslint-disable-next-line no-console
    console.log("useFlatRate (job/form):", {
      job: !!job?.useFlatRate,
      form: !!form?.useFlatRate,
    });
    // eslint-disable-next-line no-console
    console.log("configs.rates keys:", Object.keys(globalRates || {}));
    // eslint-disable-next-line no-console
    console.log(
      "configs.rates snapshot:",
      safePick(globalRates, rateKeysToCheck),
    );
    // eslint-disable-next-line no-console
    console.log(
      "client.rates snapshot:",
      safePick(clientRates, rateKeysToCheck),
    );
    // eslint-disable-next-line no-console
    console.log("job persisted rates:", safePick(job, rateKeysToCheck));
    // eslint-disable-next-line no-console
    console.log("form rates:", safePick(form || {}, rateKeysToCheck));
    // eslint-disable-next-line no-console
    console.log("computed renderRate()", {
      fuelRate: renderRate("fuelRate"),
      baseRate: renderRate("baseRate"),
      flatRate: renderRate("flatRate"),
      fuelFallback: renderRate("fuel"),
      baseFallback: renderRate("base"),
    });
    // eslint-disable-next-line no-console
    console.groupEnd();
  }, [
    shouldDebugRates,
    job?._id,
    appType,
    configs?.rates,
    job?.client?.rates,
    job?.useGlobalRates,
    form?.useGlobalRates,
    job?.useFlatRate,
    form?.useFlatRate,
    form?.fuelRate,
    form?.baseRate,
    form?.flatRate,
  ]);

  function applyRatesForToggles({ nextUseGlobalRates, nextUseFlatRate }) {
    const fuel =
      renderRateFromSource("fuelRate", nextUseGlobalRates) ??
      renderRateFromSource("fuel", nextUseGlobalRates);
    const base =
      renderRateFromSource("baseRate", nextUseGlobalRates) ??
      renderRateFromSource("base", nextUseGlobalRates);

    updateForm({ id: "fuelRate", value: fuel });
    updateForm({ id: "baseRate", value: base });

    if (nextUseFlatRate) {
      updateForm({
        id: "flatRate",
        value: renderRateFromSource("flatRate", nextUseGlobalRates),
      });
    }

    if (shouldDebugRates) {
      // eslint-disable-next-line no-console
      console.log("[JobShow][RatesDebug] applyRatesForToggles()", {
        nextUseGlobalRates,
        nextUseFlatRate,
        computed: {
          fuel,
          base,
          flatRate: nextUseFlatRate
            ? renderRateFromSource("flatRate", nextUseGlobalRates)
            : undefined,
        },
      });
    }
  }

  // Draft-only: don't persist until user hits Save in the floating bar.
  function handleGlobalRates({ target }) {
    const nextUseGlobalRates = !!target.checked;
    updateForm({ id: "useGlobalRates", value: nextUseGlobalRates });
    applyRatesForToggles({
      nextUseGlobalRates,
      nextUseFlatRate: !!form.useFlatRate,
    });

    if (shouldDebugRates) {
      // eslint-disable-next-line no-console
      console.log(
        "[JobShow][RatesDebug] toggle useGlobalRates ->",
        nextUseGlobalRates,
      );
    }
  }

  // Draft-only: don't persist until user hits Save in the floating bar.
  function handleFlatRate({ target }) {
    const nextUseFlatRate = !!target.checked;
    updateForm({ id: "useFlatRate", value: nextUseFlatRate });

    if (nextUseFlatRate) {
      updateForm({
        id: "flatRate",
        value: renderRateFromSource("flatRate", !!form.useGlobalRates),
      });
    } else {
      // When switching back to per-mile, make sure fields are populated.
      applyRatesForToggles({
        nextUseGlobalRates: !!form.useGlobalRates,
        nextUseFlatRate,
      });
    }

    if (shouldDebugRates) {
      // eslint-disable-next-line no-console
      console.log(
        "[JobShow][RatesDebug] toggle useFlatRate ->",
        nextUseFlatRate,
      );
    }
  }

  const rateToggleChanges = useMemo(() => {
    const changes = [];
    if (!!form.useGlobalRates !== !!rateTogglesBaseline.useGlobalRates) {
      changes.push({
        id: "useGlobalRates",
        label: "Global Rates",
        value: !!form.useGlobalRates,
      });
    }
    if (!!form.useFlatRate !== !!rateTogglesBaseline.useFlatRate) {
      changes.push({
        id: "useFlatRate",
        label: "Flat Rate",
        value: !!form.useFlatRate,
      });
    }
    return changes;
  }, [
    form.useGlobalRates,
    form.useFlatRate,
    rateTogglesBaseline.useGlobalRates,
    rateTogglesBaseline.useFlatRate,
  ]);

  async function saveRateToggleChanges() {
    if (!rateToggleChanges.length || savingRateToggles) return;
    if (!form?._id) return;

    try {
      setSavingRateToggles(true);

      const data = {
        id: "rate-toggles",
        useGlobalRates: !!form.useGlobalRates,
        useFlatRate: !!form.useFlatRate,
      };

      // Match prior behavior: when toggles are persisted, rates should snap to the selected source.
      data.fuelRate =
        renderRateFromSource("fuelRate", data.useGlobalRates) ??
        renderRateFromSource("fuel", data.useGlobalRates);
      data.baseRate =
        renderRateFromSource("baseRate", data.useGlobalRates) ??
        renderRateFromSource("base", data.useGlobalRates);
      if (data.useFlatRate) {
        data.flatRate = renderRateFromSource("flatRate", data.useGlobalRates);
      }

      const res = await updateGlobalJobRates({ id: form._id, data }).unwrap();
      if (res?.data) {
        setFormInit(res.data);
        setRateTogglesBaseline({
          useGlobalRates: !!res.data.useGlobalRates,
          useFlatRate: !!res.data.useFlatRate,
        });
      } else {
        // fallback: treat current draft as baseline
        setRateTogglesBaseline({
          useGlobalRates: !!form.useGlobalRates,
          useFlatRate: !!form.useFlatRate,
        });
      }
    } finally {
      setSavingRateToggles(false);
    }
  }

  function discardRateToggleChanges() {
    updateForm({
      id: "useGlobalRates",
      value: !!rateTogglesBaseline.useGlobalRates,
    });
    updateForm({
      id: "useFlatRate",
      value: !!rateTogglesBaseline.useFlatRate,
    });
    applyRatesForToggles({
      nextUseGlobalRates: !!rateTogglesBaseline.useGlobalRates,
      nextUseFlatRate: !!rateTogglesBaseline.useFlatRate,
    });
  }

  const jobShowActions = useMemo(
    () =>
      [
        {
          value: "edit",
          appTypes: ["trucking", "serviceRepair"],
          label: editing && !editDirty ? "Cancel edit" : "Edit",
          onClick: () => {
            // A clean edit session has no bottom bar yet, so this item is the way out.
            if (editing) {
              if (!editDirty) discardEdits();
              return;
            }
            if (job?._id && form?._id && String(job._id) === String(form._id)) {
              setEditBaselineKey(String(job._id));
              setEditBaselineForm(form);
            }
            setisEditing(true);
          },
        },
        {
          value: "delete",
          appTypes: ["trucking", "serviceRepair"],
          label: "Delete",
          onClick: () => setDeleteVisible(true),
        },
        {
          value: "template",
          appTypes: ["serviceRepair"],
          label: "Create Template",
          onClick: () => setShowTemplateModal(true),
        },
      ].filter((i) => {
        if (!i.appTypes.includes(appType)) return false;
        if (isNew && (i.value === "edit" || i.value === "delete")) return false;
        return true;
      }),
    [appType, job?._id, form?._id, isNew, job.status, editing, editDirty],
  );

  useEffect(() => {
    if (isJobError || !job._id) {
      crumbs.setLoading(false);
      return;
    }
    crumbs.setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isJobError, job._id]);

  useEffect(() => {
    return () => {
      setCrumbsRightContent(null);
    };
  }, [setCrumbsRightContent]);

  // First useEffect for form initialization
  useEffect(() => {
    if (job._id) {
      const init = {
        ...job,
        // IMPORTANT:
        // Use nullish coalescing so valid numeric 0 does not get treated as "missing".
        fuelRate:
          job.fuelRate ??
          job.fuel ??
          renderRate("fuelRate") ??
          renderRate("fuel"),
        baseRate:
          job.baseRate ??
          job.base ??
          renderRate("baseRate") ??
          renderRate("base"),
        flatRate: job.flatRate ?? renderRate("flatRate"),
        transactionDate: job.transactionDate,
      };

      crumbs.updateCrumbName({
        name: jobId,
        path: "/jobs/" + job._id,
      });
      crumbs.setLoading(false);
      setFormInit(init);
      setTimeout(() => setLoading(false), 500);

      setRateTogglesBaseline({
        useGlobalRates: !!init.useGlobalRates,
        useFlatRate: !!init.useFlatRate,
      });
    }
  }, [job._id]);

  /**
   * When configs arrive after the job loads, hydrate missing rate fields.
   * This avoids "rates not setting" when the job object doesn't have explicit rates yet
   * (common for new jobs) and config fetch resolves after the initial form init.
   *
   * We do NOT overwrite user edits or existing values.
   */
  useEffect(() => {
    if (!job?._id) return;
    if (hasUnsavedRates) return;
    // Only guard against cross-job leakage when form has an id.
    if (form?._id && String(form._id) !== String(job._id)) return;

    // Only apply defaults for fields that are truly missing.
    const nextFuel =
      form.fuelRate ??
      job.fuelRate ??
      job.fuel ??
      renderRate("fuelRate") ??
      renderRate("fuel");
    const nextBase =
      form.baseRate ??
      job.baseRate ??
      job.base ??
      renderRate("baseRate") ??
      renderRate("base");
    const nextFlat = form.flatRate ?? job.flatRate ?? renderRate("flatRate");

    if (shouldDebugRates) {
      // eslint-disable-next-line no-console
      console.log("[JobShow][RatesDebug] hydrate-missing-rates check", {
        jobId: job?._id,
        current: {
          fuelRate: form.fuelRate,
          baseRate: form.baseRate,
          flatRate: form.flatRate,
        },
        next: { fuelRate: nextFuel, baseRate: nextBase, flatRate: nextFlat },
      });
    }

    if (isEmptyRateValue(form.fuelRate) && nextFuel != null) {
      updateForm({ id: "fuelRate", value: nextFuel });
    }
    if (isEmptyRateValue(form.baseRate) && nextBase != null) {
      updateForm({ id: "baseRate", value: nextBase });
    }
    if (isEmptyRateValue(form.flatRate) && nextFlat != null) {
      updateForm({ id: "flatRate", value: nextFlat });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    job?._id,
    editing,
    hasUnsavedRates,
    form?._id,
    form?.fuelRate,
    form?.baseRate,
    form?.flatRate,
    configs?.rates,
    job?.client?.rates,
    job?.base,
    job?.fuel,
    job?.baseRate,
    job?.fuelRate,
    job?.useGlobalRates,
    job?.useFlatRate,
    shouldDebugRates,
  ]);

  // Separate useEffect for right content
  useEffect(() => {
    const openJob = (id) => {
      if (!id) return;
      navigate(`/jobs/${id}${location.search}${location.hash}`);
    };

    setCrumbsRightContent(
      <div className="flex items-center gap-1">
        <JobNeighborButton
          label="Previous job"
          disabled={!previousJobId}
          onClick={() => openJob(previousJobId)}
        >
          <ChevronLeftIcon className="h-5 w-5" />
        </JobNeighborButton>
        <JobNeighborButton
          label="Next job"
          disabled={!nextJobId}
          onClick={() => openJob(nextJobId)}
        >
          <ChevronRightIcon className="h-5 w-5" />
        </JobNeighborButton>
        <Dropdown
          align="right"
          trigger={
            <EllipsisHorizontalIcon className="cursor-pointer text-gray-800 w-6" />
          }
          data={jobShowActions}
        />
      </div>,
    );
  }, [
    setCrumbsRightContent,
    jobShowActions,
    previousJobId,
    nextJobId,
    navigate,
    location.search,
    location.hash,
  ]);

  useEffect(() => {
    if (
      job.assignedTo &&
      JSON.stringify(assignedUsers) !== JSON.stringify(job.assignedTo)
    ) {
      setAssignedUsers(
        job.assignedTo
          .filter((userData) => userData)
          .map((userData) => ({
            ...userData,
            id: userData?._id || userData?.id, // Ensure we have a consistent id field
          })),
      );
      setFormInit({ ...form, assignedTo: job.assignedTo });
    }
  }, [job.assignedTo, job.assignedTo?.length]);

  /**
   * Tabs
   *
   * NOTE:
   * This must stay reactive to `form` changes. Memoizing without including `form`
   * causes stale props to be passed into `JobInfo` (which is why rates were computing
   * correctly but the inputs stayed blank).
   */
  const tabs = [
    {
      id: "Info",
      appTypes: ["trucking", "serviceRepair"],
      content: (
        <JobInfo
          form={{ form, setFormInit, clearForm, updateForm }}
          job={jobWithAllLegs}
          editing={editing}
          isJobActive={isJobActive}
          isNew={isNew}
          isPending={isPending}
          loading={isQueryLoading || loading}
          setLoading={setLoading}
          onUnsavedRatesChange={setHasUnsavedRates}
          rateToggleChanges={rateToggleChanges}
          onSaveRateToggleChanges={saveRateToggleChanges}
          onDiscardRateToggleChanges={discardRateToggleChanges}
          savingRateToggles={savingRateToggles}
          inlineLegEdit={sheetPreviewOpen}
          onRateChangesSaved={() => {
            if (editing && !isNew) {
              setisEditing(false);
            }
          }}
          updateJobData={updateJobData}
          approveJobItems={approveJobItems}
          deferLegCreate={deferLegCreate}
          pendingLegCreates={pendingLegCreates}
          onAddPendingLeg={(pending) => {
            setPendingLegCreates((prev) => [...prev, pending]);
          }}
          onRemovePendingLeg={(pendingId) => {
            setPendingLegCreates((prev) =>
              (prev || []).filter((p) => (p?.id || p?.label) !== pendingId),
            );
          }}
          onUpdatePendingLeg={(nextPending) => {
            setPendingLegCreates((prev) =>
              (prev || []).map((p) => {
                const key = p?.id || p?.label;
                const nextKey = nextPending?.id || nextPending?.label;
                return key === nextKey ? nextPending : p;
              }),
            );
          }}
          />
      ),
    },
    // {
    //   id: "Equipment",
    //   content: (
    //     <JobEquipment
    //       form={{ form, setFormInit, clearForm, updateForm }}
    //       job={job}
    //     />
    //   ),
    // },
    {
      id: "Quotes",
      appTypes: ["serviceRepair"],
      hidden: "New,Pending,Booked",
      featureKey: "jobs.quotes.enabled",
      content: (
        <JobQuotes
          form={{ form, setFormInit, clearForm, updateForm }}
          job={job}
          quotes={getItemsByType("Quote")}
          isJobActive={isJobActive}
          editing={editing}
          isNew={isNew}
          isPending={isPending}
          loading={isQueryLoading || loading}
          setLoading={setLoading}
          updateJobData={updateJobData}
        />
      ),
    },
    // {
    //   id: "Legs",
    //   title: `Legs (${job.legs?.length || 0})`,
    //   content: (
    //     <LegsTable
    //       job={job}
    //       setVisible={setLegModalVisible}
    //       visible={legModalVisible}
    //       renderHeader={() => renderHeader(job)}
    //       form={legForm}
    //       editing={editing}
    //     />
    //   ),
    //   hidden: "New,Pending,Booked",
    // },
    {
      id: "Accessories",
      appTypes: ["trucking", "serviceRepair"],
      featureKey: "jobs.accessories.enabled",
      content: (
        <Accessories
          appType={appType}
          accessories={job?.accessories}
          user={user}
          jobId={job?._id}
          editing={editing}
        />
      ),
    },
    {
      id: "Tasks",
      appTypes: ["trucking", "serviceRepair"],
      hidden: "New,Pending",
      featureKey: "jobs.tasks.enabled",
      content: job?._id ? (
        <div className="p-4">
          <TaskList
            parentType="job"
            parentId={job._id}
            editable={canEditJobs}
            showHeader={true}
          />
        </div>
      ) : null,
    },
    // {
    //   id: "Files",
    //   content: (
    //     <Files
    //       user={user}
    //       jobId={job?._id}
    //       files={job?.files}
    //       editing={editing}
    //     />
    //   ),
    //   hidden: "New",
    // },
  ].filter((item) => {
    if (item.featureKey && !isFeatureEnabled(item.featureKey)) {
      return false;
    }
    if (!item.appTypes.includes(appType)) {
      return false;
    }
    if (item.hidden?.includes(job.status)) {
      return false;
    }
    return true;
  });

  function handleTabClick(_e, _item, path) {
    // location.hash = e.target.innerHTML;
    navigate(path);
  }

  function getDefaultTab() {
    if (location.hash) {
      const tabHash = location.hash.slice(1);
      return tabs.findIndex((tab) => tab.id === tabHash);
    }
    return;
  }

  const title = (
    <div className="flex items-center">
      <EditableText
        className="ml-1"
        iconClasses="h-3 w-3"
        id="jobId"
        value={jobId}
        size="md"
        displayText={(v) => (
          <span className="text-gray-800 font-semibold text-lg">{v}</span>
        )}
        disabled={!canEditOrAssign}
        loading={modalLoad}
        onSave={(e) => updateJobData({ ...e.data, loadNumber: e.value })}
      />
      {job.status && (
        <StatusBadge
          status={job.status}
          component="job"
          size="lg"
          className="ml-2"
        />
      )}
      {job?.metadata?.import?.source === "google_drive" && (
        <Badge
          color="blue"
          label="Imported from Drive"
          size="lg"
          className="ml-2"
        />
      )}
      {canEditOrAssign && canViewInvoices && job.invoiceId?.invoiceId && (
        <Badge
          // color={stage?.color}
          link={`/invoices/${job.invoiceId._id}`}
          label={`Invoice: ${job.invoiceId.invoiceId}`}
          size="lg"
          className="ml-2"
        />
      )}
      {job?._id && (
        <JobTagsInline
          job={job}
          editable={canEditOrAssign}
          onChangeTags={(tags) => updateJobData({ tags })}
          badgeSize="lg"
        />
      )}
    </div>
  );

  function getNextStatus() {
    return getNextStage(
      normalizeJobStatusForStages(job.status, appType),
      { job, form },
      appType,
    ).title;
  }

  /* 
    Update Job 
  */
  async function updateJobData(data) {
    setModalLoad(true);

    const applyJobRateUpdates = async (payload) => {
      if (!job?._id) return sanitizeJobUpdateData(payload || {});
      if (!payload || typeof payload !== "object") return {};

      const rateUpdates = getRateUpdatePayloads({ job, data: payload });
      for (const update of rateUpdates) {
        await updateJobRates({
          id: job._id,
          data: {
            [update.id]: update.value,
            charge: update.charge,
            id: update.id,
          },
        }).unwrap();
      }

      // Rates should only update through the dedicated rates endpoints.
      return sanitizeJobUpdateData(payload);
    };

    const updateData = {
      id: _id,
      data: {
        ...(await applyJobRateUpdates(data)),
      },
    };

    try {
      setMainActionModal(false);
      const res = await updateJob(updateData).unwrap();

      if (res?.status) {
        // Keep legacy slice in sync (other parts of the app still read from it).
        dispatch(jobUpdated(res.data));

        // Keep the local form in sync immediately (don't wait on any background updates).
        if (res?.data) {
          setFormInit(res.data);
        }

        if (editing) {
          setisEditing(false);
        }
      }

      return res;
    } catch (err) {
      // Avoid "stuck loading" if the request fails.
      toast(
        err?.data?.message || err?.message || "Failed to save job changes",
        {
          type: "error",
          toastId: "job-save-error",
        },
      );
      throw err;
    } finally {
      setModalLoad(false);
    }
  }

  async function updateJobStatus(e) {
    let updateData = { ...form, items: job.items, legs: job.legs };

    if (updateData.selectedItems?.length) {
      await approveJobItems(updateData.selectedItems);
    }

    if (assignedUsers.length) {
      updateData.assignedTo = assignedUsers.map((i) => i._id);
    }

    if (stage.id === "new" || editing) {
      updateData.transactionDate = form.transactionDate;
    }

    // For trucking: Scheduled stage should keep the main header action as "Dispatch".
    // If the user is editing fields, they must Save/Discard via the footer before dispatching.
    if (isTruckingScheduledStage && editing && editDirty) {
      toast("You have unsaved changes. Save or discard before dispatching.", {
        type: "info",
        toastId: "job-dispatch-unsaved",
      });
      return;
    }

    if (!editing || isTruckingScheduledStage) {
      updateData.status = getNextStatus();
    }

    await updateJobData(updateData);
  }

  /**
   * Persist edits the user made while in edit mode and exit edit mode.
   *
   * Works for any stage / appType. `updateJobData` handles rate fields via
   * dedicated endpoints and exits edit mode on success. Pending leg drafts
   * are trucking-only and the loop is a no-op when there are none.
   */
  async function saveEdits() {
    if (!editing) return;
    if (!editDirty) {
      setisEditing(false);
      return;
    }

    let updateData = { ...form, items: job.items, legs: job.legs };
    if (assignedUsers.length) {
      updateData.assignedTo = assignedUsers.map((i) => i._id);
    }
    await updateJobData(updateData);

    if (pendingLegCreates.length > 0) {
      for (const pending of pendingLegCreates) {
        const mainArgs = pending?.mainArgs;
        if (!mainArgs?.data || !mainArgs?.jobId || !mainArgs?.client) continue;
        const mainRes = await addLeg(mainArgs).unwrap();

        if (pending?.handoffArgs?.data) {
          const handoffArgs = {
            ...pending.handoffArgs,
            data: {
              ...pending.handoffArgs.data,
              handoffParent: mainRes?.data?.leg?._id,
            },
          };
          await addLeg(handoffArgs).unwrap();
        }
      }
      setPendingLegCreates([]);
    }
  }

  /**
   * Revert any draft edits and exit edit mode without saving.
   * Resets the form to the captured baseline and clears pending leg drafts.
   */
  function discardEdits() {
    if (!editing) return;
    if (!editBaselineForm) {
      setisEditing(false);
      return;
    }
    setFormInit(editBaselineForm, { reset: true });
    setPendingLegCreates([]);
    setisEditing(false);
  }

  function handleDiscardClick() {
    // Only confirm if there is something to lose (field changes or draft legs).
    if (editDirty || pendingLegCreates.length > 0) {
      setDiscardConfirmOpen(true);
      return;
    }
    discardEdits();
  }

  function renderButtonTitle() {
    const res = getStage(
      normalizeJobStatusForStages(job.status, appType),
      appType,
    );
    if (appType === "trucking" && stageId === "dispatched") {
      return hasTruckingDispatchableLegs ? "Dispatch" : terms.completeJob;
    }
    return res?.button;
  }

  const actions = useMemo(() => {
    const isMainActionDisabled =
      modalLoad || jobUpdating || globalRatesUpdating || secondaryButtonLoading;
    const isTruckingScheduleAction =
      appType === "trucking" && !editing && stage?.id === "new";

    return (
      canEditOrAssign && (
        <>
          {(stage?.secondaryButton ||
            ((isJobActive || isCompleted) && !job.invoiceId)) && (
            <Button
              size="sm"
              onClick={() => setCreateInvoiceVisible(true)}
              type="alternative"
            >
              Create Invoice
            </Button>
          )}
          {(stage?.button || (editing && appType !== "trucking")) && (
            <Button
              split
              disabled={
                isMainActionDisabled ||
                // Don't allow any stage/save actions if user has pending rate edits
                // that haven't been committed yet.
                (appType === "trucking" &&
                  hasUnsavedRates &&
                  !isTruckingScheduleAction)
              }
              onClick={() => {
                if (
                  appType === "trucking" &&
                  hasUnsavedRates &&
                  !isTruckingScheduleAction
                )
                  return;
                if (isMainActionDisabled) return;
                const stageId = normalizeJobStatusForStages(
                  job.status,
                  appType,
                );
                const isTruckingScheduleAction =
                  appType === "trucking" && !editing && stageId === "new";
                const isTruckingScheduledDispatchAction =
                  appType === "trucking" && stageId === "scheduled";
                const isTruckingDispatchedButStillDispatchable =
                  appType === "trucking" &&
                  stageId === "dispatched" &&
                  hasTruckingDispatchableLegs;
                const isTruckingDispatchedAndReadyToCloseout =
                  appType === "trucking" &&
                  stageId === "dispatched" &&
                  !hasTruckingDispatchableLegs;

                if (isTruckingScheduledDispatchAction && editing && editDirty) {
                  toast("Save or discard changes before dispatching.", {
                    type: "info",
                    toastId: "job-dispatch-unsaved",
                  });
                  return;
                }

                if (isTruckingScheduleAction) {
                  setScheduleJobModalOpen(true);
                } else if (
                  isTruckingScheduledDispatchAction ||
                  isTruckingDispatchedButStillDispatchable
                ) {
                  if (!isDispatchEnabled) {
                    toast("Dispatch is disabled for this tenant.", {
                      type: "info",
                      toastId: "job-dispatch-disabled",
                    });
                    return;
                  }
                  setDispatchJobModalOpen(true);
                } else if (isTruckingDispatchedAndReadyToCloseout) {
                  setCompleteJobModalOpen(true);
                } else {
                  setMainActionModal(true);
                }
              }}
              className="min-w-24"
              size="sm"
            >
              {editing &&
              appType !== "trucking" &&
              stage?.title !== getStage(1, appType).title
                ? "Save"
                : renderButtonTitle()}
            </Button>
          )}
        </>
      )
    );
  }, [
    stage,
    isTruckingScheduledStage,
    editDirty,
    isJobActive,
    job.invoiceId,
    editing,
    isNew,
    modalLoad,
    jobUpdating,
    globalRatesUpdating,
    secondaryButtonLoading,
    hasUnsavedRates,
    appType,
    isDispatchEnabled,
    terms.completeJob,
  ]);

  const rateActions = canEditJobs && (editing || isNew) && (
    <>
      <div className="flex items-center space-x-2">
        <span className="text-gray-800">Global Rates:</span>
        <Toggle
          id="useGlobalRates"
          size="sm"
          checked={form.useGlobalRates}
          onChange={(e) => handleGlobalRates({ target: e.target })}
        />
      </div>
      <div className="flex items-center  space-x-2">
        <span className="text-gray-800">Flat Rate:</span>
        <Toggle
          id="useFlatRate"
          size="sm"
          checked={form.useFlatRate}
          onChange={(e) => handleFlatRate({ target: e.target })}
        />
      </div>
    </>
  );

  const saveAssignedUsers = async (users) => {
    try {
      await updateJobData({ assignedTo: users.map((i) => i._id || i.id) });
      setAssignedUsers(users);
    } catch (error) {
      console.error("Error saving assigned users:", error);
    }
  };

  const handleEmployeesSearch = (term) => {
    handleSearch({ term, useSearch: !!term });
  };

  const assignedTo = useMemo(
    () => (
      <AssignedToSelect
        onChange={saveAssignedUsers}
        value={assignedUsers}
        users={allEmployees.map((emp) => ({
          ...emp,
          id: emp._id,
        }))}
        isEditable={canEditOrAssign}
        onLoadMore={handleLoadMoreEmployees}
        hasMore={employeesData?.hasNext}
        loading={loadingEmployees || fetchingMoreEmployees}
        onSearch={handleEmployeesSearch}
        onCancel={() => setAssignedUsers(assignedUsers)}
      />
    ),
    [
      assignedUsers,
      allEmployees,
      loadingEmployees,
      fetchingMoreEmployees,
      employeesData?.hasNext,
    ],
  );

  async function createInvoice() {
    const invoiceData = { jobs: [{ _id: job._id }], client: job.client };
    const res = await addInvoice(invoiceData).unwrap();

    toast(res.message, {
      type: res.status,
      toastId: "inv-job-create",
    });
    setCreateInvoiceVisible(false);
  }

  let headerActions;
  switch (appType) {
    case "serviceRepair":
      headerActions = assignedTo;
      break;
    default:
      headerActions = rateActions;
  }

  const renderHeader = useMemo(() => {
    return (headerProps) => {
      if (!headerProps) return null;

      return (
        <div ref={headerProps.headerRef} onClick={(e) => e.stopPropagation()}>
          <header className="flex items-center justify-between px-3 py-2 text-semibold text-gray-100">
            <div>{headerProps.title}</div>
            <div className="flex items-center space-x-3">
              <div className="md:flex items-center space-x-3 hidden">
                {headerActions}
              </div>
              <div className="hidden md:flex items-center space-x-3">
                {headerProps.actions}
              </div>
              {headerProps.setOpen && (
                <div
                  onClick={(e) => {
                    e.stopPropagation();
                    headerProps.setOpen(true);
                  }}
                  className="hover:bg-gray-100 cursor-pointer p-2 rounded-lg block lg:hidden"
                >
                  <ChevronDoubleLeftIcon className="w-5 text-gray-800" />
                </div>
              )}
            </div>
          </header>
          {headerProps.actions && (
            <div className="flex justify-between md:hidden space-x-5 px-3 py-3 bg-white border-b">
              {headerProps.actions}
            </div>
          )}
        </div>
      );
    };
  }, [headerActions]);

  async function handleCreateTemplate(data) {}

  const sidebarContent = ({ ...sidebarProps }) => {
    return (
      <SidebarContent
        updateForm={updateForm}
        form={form}
        // handleUpdate={handleUpdate}
        onFormChange={onFormChange}
        job={job}
        {...sidebarProps}
      />
    );
  };

  const modalFooter = (
    <>
      <Button onClick={() => setMainActionModal(false)} type="alternative">
        Cancel
      </Button>
      <Button
        loading={modalLoad}
        disabled={modalLoad || !isValid}
        onClick={updateJobStatus}
        type="primary"
      >
        Submit
      </Button>
    </>
  );

  const modalCreateFooter = (
    <>
      <Button onClick={() => setCreateInvoiceVisible(false)} type="alternative">
        Cancel
      </Button>
      <Button
        loading={addingInvoice}
        disabled={addingInvoice}
        onClick={createInvoice}
        type="primary"
      >
        Submit
      </Button>
    </>
  );

  // async function handleDelete() {
  //   const res = await deleteJobs({ data: [job._id] });

  //   toast(res.data.message, {
  //     type: res.data.status,
  //     toastId: "delete-jobs",
  //   });

  //   if (res.data.status === "success") {
  //     navigate("/jobs");
  //   }

  //   return res.data;
  // }

  const modalContent = useMemo(() => {
    const modalProps = {
      form,
      setFormInit,
      clearForm,
      updateForm,
      updateJobData,
      employees: allEmployees,
      handleLoadMoreEmployees,
      handleEmployeesSearch,
      employeesQuery: employeesData,
      assignedUsers,
      setAssignedUsers,
    };

    // Get the current stage based on the job status
    const currentStage = getStage(
      normalizeJobStatusForStages(job.status, appType),
      appType,
    );

    if (currentStage) {
      const stageContent = getModalContentForStage(currentStage, modalProps);
      if (stageContent) return stageContent;
    }

    // Fallback messages
    if (editing) return `Are you sure you want to save this ${terms.jobLower}?`;
    const stageId = normalizeJobStatusForStages(job.status, appType);

    // Use stageId-based fallbacks (keeps compatibility with canonical snake_case + legacy Title Case)
    if (appType === "trucking") {
      if (stageId === "new")
        return `Are you sure you want to schedule this ${terms.jobLower}?`;
      if (stageId === "scheduled")
        return `Are you sure you want to dispatch this ${terms.jobLower}?`;
      if (stageId === "dispatched")
        return `Are you sure you want to start this ${terms.jobLower}?`;
      if (stageId === "in_progress")
        return `Are you sure you want to mark this ${terms.jobLower} complete?`;
      if (stageId === "completed")
        return `Are you sure you want to create an invoice for this ${terms.jobLower}?`;
    } else {
      // serviceRepair
      if (stageId === "new")
        return "Are you sure you want to publish this job?";
      if (stageId === "pending")
        return "Are you sure you want to book this job?";
      if (stageId === "booked")
        return "Are you sure you want to assign this job?";
      if (stageId === "assigned")
        return "Are you sure you want to start this job?";
      if (stageId === "inProgress")
        return "Are you sure you want to mark this job complete?";
      if (stageId === "completed")
        return "Are you sure you want to create an invoice for this job?";
    }
    return "";
  }, [
    job.status,
    editing,
    form,
    allEmployees,
    employeesData,
    assignedUsers,
    terms.jobLower,
    // Add other dependencies that affect the modal content
  ]);

  const hasSheetPreview =
    Array.isArray(job?.metadata?.extractedData?.sheets) &&
    job.metadata.extractedData.sheets.length > 0;

  const renderTabHeader = useMemo(() => {
    return (headerProps) => (
      <div className="flex w-full flex-row items-center justify-between bg-white">
        <div className="min-w-0 flex-1">{headerProps}</div>

        <div className="flex shrink-0 flex-row items-center gap-3 mr-4">
          {hasSheetPreview && (
            <Button
              type="light"
              size="sm"
              onClick={() => setSheetPreviewOpen((open) => !open)}
            >
              {previewFileButtonLabel(sheetPreviewOpen)}
            </Button>
          )}
          <Dropdown
            align="right"
            closeOnClick
            widthClasses="w-80"
            className="flex"
            trigger={
              <div
                className="flex flex-row items-center cursor-pointer rounded-md px-2 py-1 transition-colors hover:bg-gray-50"
                title="View client details"
              >
                <Avatar
                  src={job.client?.avatar}
                  name={job.client?.name}
                  size="xs"
                />
                <div className="ml-2 text-md text-gray-700">
                  {job.client?.name}
                </div>
                <ChevronDownIcon className="ml-1 h-4 w-4 text-gray-500" />
              </div>
            }
            data={<ClientCard client={job.client} />}
            itemClick={onFormChange}
          />
        </div>
      </div>
    );
  }, [job.client, onFormChange, hasSheetPreview, sheetPreviewOpen]);

  /** Slim bar during PATCH updates only (not background polling refetch — that no longer blanks the header). */
  const showJobSyncBar = !!job._id && isJobUpdating;

  // Handle loading state
  if (!job._id && !isJobError) {
    return (
      <div className="flex items-center justify-center h-full">
        <Spinner size="lg" />
      </div>
    );
  }

  // Handle error / missing job (invalid id, 404)
  if (isJobError || !job._id) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <p className="text-lg font-medium text-gray-900">
            Error loading job data.
          </p>
          <p className="mt-1 text-sm text-gray-500">
            This load could not be found or the id is invalid.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="relative h-full min-h-0 flex flex-col">
      {showJobSyncBar && (
        <div
          className="pointer-events-none absolute left-0 right-0 top-0 z-30 h-0.5 overflow-hidden bg-slate-200/90"
          role="status"
          aria-live="polite"
          aria-label="Refreshing job"
        >
          <div className="h-full w-[30%] rounded-full bg-primary-500 shadow-sm animate-job-sync-slide" />
        </div>
      )}
      <div className="flex items-center justify-between px-4 flex-shrink-0">
        <div className="flex items-center space-x-24"></div>
      </div>
      {appType === "trucking" && (
        <JobRequirementAlerts legs={legsForDispatch} configs={configs} />
      )}
      <DetailSidebarLayout
        layout="page"
        title={title}
        header={renderHeader}
        actions={actions}
        hideSidebar={!canEditJobs}
        splitStorageKey="job-show-activity-sidebar"
        sidebarContent={sidebarContent}
        sidebarTitle={<h2 className="text-xl bg-white py-1">Activity</h2>}
        tabs={tabs}
        tabsProps={{
          useSearchParams: true,
          tabHeader: renderTabHeader,
          id: "job-show",
          onClick: handleTabClick,
          aside:
            sheetPreviewOpen && hasSheetPreview ? (
              <JobSheetPreview
                job={job}
                onClose={() => setSheetPreviewOpen(false)}
              />
            ) : null,
        }}
      />
      {mainActionModal && (
        <JobActionModal
          visible={mainActionModal}
          setVisible={setMainActionModal}
          modalContent={modalContent}
          stage={stage}
          modalLoad={modalLoad}
          isValid={isValid}
          onSubmit={updateJobStatus}
        />
      )}
      {scheduleJobModalOpen && (
        <ScheduleJobModal
          visible={scheduleJobModalOpen}
          setVisible={setScheduleJobModalOpen}
          jobId={job?._id}
        />
      )}
      {isDispatchEnabled && dispatchJobModalOpen && (
        <DispatchJobModal
          visible={dispatchJobModalOpen}
          setVisible={setDispatchJobModalOpen}
          jobId={job?._id}
          jobLabel={jobId}
        />
      )}
      {completeJobModalOpen && (
        <CompleteJobModal
          visible={completeJobModalOpen}
          setVisible={setCompleteJobModalOpen}
          jobId={job?._id}
          jobLabel={jobId}
          client={job?.client}
          invoiceId={job?.invoiceId}
          legs={legsForDispatch || []}
        />
      )}
      {/*
        Edit-mode footer.
        Stays hidden until something actually changed, so entering edit mode
        does not pop a bar before the user edits. Rate edits use this same bar
        while edit mode is open.
      */}
      {editing &&
        editDirty &&
        editBaselineForm &&
        job?._id &&
        form?._id &&
        String(job._id) === String(form._id) && (
          <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 w-[calc(100%-2rem)] max-w-3xl pointer-events-none">
            <div className="pointer-events-auto border border-gray-200 bg-white/95 backdrop-blur rounded-xl shadow-lg px-4 py-3">
              <div className="flex items-center justify-between gap-4">
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-gray-900">
                    {`Ready to update: ${editChanges.length} change${
                      editChanges.length === 1 ? "" : "s"
                    }`}
                  </div>
                  <div className="text-xs text-gray-600 mt-1">
                    {!!pendingLegCreates?.length && (
                      <div className="mb-1 text-amber-800">
                        You’re editing this {terms.jobLower}. Draft legs will not be added
                        until you click{" "}
                        <span className="font-semibold">Save changes</span>.
                      </div>
                    )}
                    <span className="line-clamp-2">
                      {editChanges
                        .slice(0, 8)
                        .map(
                          (c) => `${c.label} → ${formatFieldValue(c.after)}`,
                        )
                        .join(" • ")}
                      {editChanges.length > 8 ? " • …" : ""}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <Button
                    type="alternative"
                    size="sm"
                    disabled={modalLoad || jobUpdating}
                    onClick={handleDiscardClick}
                  >
                    {editDirty || pendingLegCreates.length > 0
                      ? "Discard"
                      : "Cancel"}
                  </Button>
                  <Button
                    size="sm"
                    loading={modalLoad || jobUpdating}
                    disabled={modalLoad || jobUpdating || !editDirty}
                    onClick={saveEdits}
                  >
                    Save changes
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      {discardConfirmOpen && (
        <Modal
          visible={discardConfirmOpen}
          setVisible={setDiscardConfirmOpen}
          onClose={() => setDiscardConfirmOpen(false)}
          title="Discard changes?"
          size="md"
          footerPosition="right"
          content={
            <div className="text-sm text-gray-700">
              This will discard your unsaved edits
              {pendingLegCreates?.length
                ? ` and remove ${pendingLegCreates.length} draft leg${
                    pendingLegCreates.length === 1 ? "" : "s"
                  }`
                : ""}
              .
            </div>
          }
          footer={
            <div>
              <Button
                type="alternative"
                className="mr-2"
                onClick={() => setDiscardConfirmOpen(false)}
              >
                Cancel
              </Button>
              <Button
                type="primary"
                onClick={() => {
                  setDiscardConfirmOpen(false);
                  discardEdits();
                }}
              >
                Discard
              </Button>
            </div>
          }
        />
      )}
      {deleteVisible && (
        <DeleteJobModal
          visible={deleteVisible}
          setVisible={setDeleteVisible}
          jobs={[job]}
        />
      )}
      {createInvoiceVisible && (
        <LazyModal>
          <Modal
            visible={createInvoiceVisible}
            setVisible={setCreateInvoiceVisible}
            onClose={() => setCreateInvoiceVisible(false)}
            title="Create Invoice"
            footer={modalCreateFooter}
            content={`Are you sure you want to create an invoice with this ${terms.jobLower}?`}
            size="md"
            footerPosition="right"
          />
        </LazyModal>
      )}
      {showTemplateModal && (
        <TemplateModal
          visible={showTemplateModal}
          setVisible={setShowTemplateModal}
          onClose={() => setShowTemplateModal(false)}
          onSubmit={handleCreateTemplate}
          type="job"
          template={{
            name: terms.jobTemplateSelectLabel,
            items:
              job?.items?.map((i) => ({
                serviceType: i.serviceType,
                priority: i.priority,
                serviceFee: i.serviceFee,
                summary: i.summary,
                hours: i.hoursWorked,
                parts: i.parts,
              })) || [],
          }}
        />
      )}
    </div>
  );
};

export default JobsShow;
